import {
	getAssociatedTokenAddressSync,
	TOKEN_2022_PROGRAM_ID,
	TOKEN_PROGRAM_ID,
	unpackAccount,
} from '@solana/spl-token';
import {
	Connection,
	PublicKey,
	ParsedAccountData,
	AccountInfo,
} from '@solana/web3.js';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import {
	useTokenAccountsStore,
	TokenAccountInfo,
	TokenAccountMap,
} from './useTokenAccountsStore';
import useCurrentWalletAdapter from '../../hooks/useCurrentWalletAdapter';
import { sleep } from 'src/utils/dateTime';
import { dlog } from '../../dev';
import { useUniqueSpotMarkets } from 'src/hooks/spotMarkets/useUniqueSpotMarkets';
/**
 * This hook will sync the token account state for all token accounts for the current wallet
 *
 * This hook does not KEEP the accounts in sync at the moment. It just refreshes them each time the wallet changes, or the refreshTokenAccounts event is emitted
 *
 * the useSPLTokenBalance hook does SUBSCRIBE to token account updates and push them back into this store. See the README.md in this folder.
 */

// Constants
const FILTER_BY_VELOCITY_SPOT_MARKETS = false; // If TRUE - will only sync the token account if it matches a drift spot market mint, otherwise all token accounts in the wallet will be synced. NOTE:: This is FALSE now because our dSOL staking flow allows staking non-drift assets into dSOL, so we require the token accounts loaded to display their balances. Note:: By "SYNC" we only mean the one-shot initial sync, not a long term subscription.
const SYNC_ONLY_EXPECTED_ACCOUNTS = true;
const TRACK_BAD_TOKEN_ACCOUNT_STATES = true;

const TOKEN_PROGRAMS_TO_FETCH = [
	TOKEN_2022_PROGRAM_ID,
	TOKEN_PROGRAM_ID,
] as const;

const TOKEN_ACCOUNT_FALLBACK_DELAY_MS = 1000;

// Lookup Maps
const TOKEN_PROGRAM_LOOKUP: Readonly<Record<TokenProgramLabel, PublicKey>> = {
	'spl-token': TOKEN_PROGRAM_ID,
	'spl-token-2022': TOKEN_2022_PROGRAM_ID,
} as const;

// Types
type TokenProgramLabel = 'spl-token' | 'spl-token-2022';
type MintToTokenAccountMap = Map<string, string>;

interface ParsedTokenInfo {
	mint: string;
	tokenAmount: {
		uiAmount: number;
	};
}

type TokenAccountResponse = {
	pubkey: PublicKey;
	account: AccountInfo<ParsedAccountData>;
};

type TokenAccountBadState = {
	mint: string;
	knownAccounts: Set<string>;
	expectedAccount: string;
	accountBalances: Map<string, number>; // Maps token account address to its balance
};

type TokenAccountBadStateMap = Map<string, TokenAccountBadState>;

/**
 * We use this hook to track if the wallet's token accounts are in a bad state and the UI will probably not work as expected. We do this by tracking all of the token accounts for a mint, and then seeing if we have any unexpected token accounts at the end of the sync.
 * @param authority
 * @returns
 */
const useDetectBadTokenAccountStates = (authority: PublicKey) => {
	const badTokenAccountStates = useRef<TokenAccountBadStateMap>(new Map());

	const addTokenAccountToBadStateTracking = (
		mintAddress: string,
		tokenAccount: TokenAccountResponse
	) => {
		if (!TRACK_BAD_TOKEN_ACCOUNT_STATES) return;

		const expectedAccount = getAssociatedTokenAddressSync(
			new PublicKey(mintAddress),
			authority,
			true,
			TOKEN_PROGRAM_LOOKUP[
				tokenAccount.account.data.program as TokenProgramLabel
			]
		);

		const parsedInfo = tokenAccount.account.data.parsed.info as ParsedTokenInfo;
		const balance = parsedInfo.tokenAmount.uiAmount;

		// Update the bad state map with new data
		if (badTokenAccountStates.current.has(mintAddress)) {
			const badState = badTokenAccountStates.current.get(mintAddress);
			badState?.knownAccounts.add(tokenAccount.pubkey.toBase58());
			badState?.accountBalances.set(tokenAccount.pubkey.toBase58(), balance);
		} else {
			badTokenAccountStates.current.set(mintAddress, {
				mint: mintAddress,
				knownAccounts: new Set([tokenAccount.pubkey.toBase58()]),
				expectedAccount: expectedAccount.toBase58(),
				accountBalances: new Map([[tokenAccount.pubkey.toBase58(), balance]]),
			});
		}
	};

	// Reset the state when the authority changes
	useEffect(() => {
		badTokenAccountStates.current = new Map();
	}, [authority]);

	return {
		addTokenAccountToBadStateTracking,
	};
};

export const useSyncTokenAccountsStore = () => {
	const uniqueSpotMarkets = useUniqueSpotMarkets();
	const velocitySpotMarketMints = useMemo(
		() => new Set(uniqueSpotMarkets.map((m) => m.mint.toString())),
		[uniqueSpotMarkets]
	);

	const wallet = useCurrentWalletAdapter();
	const authority = wallet?.publicKey;
	const connection = useVelocityStore((s) => s.connection.current);
	const setLastSyncedWallet = useTokenAccountsStore(
		(s) => s.setLastSyncedWallet
	);
	const setTokenAccountsStore = useTokenAccountsStore((s) => s.set);
	const eventEmitter = useVelocityStore((s) => s.appEventEmitter);
	const handledMintsToTokenAccounts = useRef<MintToTokenAccountMap>(new Map());

	const { addTokenAccountToBadStateTracking } = useDetectBadTokenAccountStates(
		authority ?? PublicKey.default
	);

	const shouldSyncTokenAccount = useCallback(
		(
			mintAddress: string,
			accountInfo: TokenAccountResponse,
			accountAuthority: PublicKey
		): boolean => {
			const mintIsForVelocitySpotMarket =
				velocitySpotMarketMints.has(mintAddress);

			// If we are filtering by velocity spot markets and this mint is not for a velocity spot market, skip it
			if (FILTER_BY_VELOCITY_SPOT_MARKETS && !mintIsForVelocitySpotMarket) {
				return false;
			}

			if (SYNC_ONLY_EXPECTED_ACCOUNTS) {
				const tokenProgramLabel = accountInfo.account.data
					.program as TokenProgramLabel;

				const expectedTokenAccount = getAssociatedTokenAddressSync(
					new PublicKey(mintAddress),
					accountAuthority,
					true,
					TOKEN_PROGRAM_LOOKUP[tokenProgramLabel]
				);

				return (
					expectedTokenAccount.toString() === accountInfo.pubkey.toString()
				);
			} else {
				// Else, we can immediately sync the token account if we have not seen this mint before
				if (!handledMintsToTokenAccounts.current.has(mintAddress)) {
					return true;
				}

				// If we have, then we prefer the new token account to the previously synced if it matches the expected token account
				const tokenProgramLabel = accountInfo.account.data
					.program as TokenProgramLabel;

				const expectedTokenAccount = getAssociatedTokenAddressSync(
					new PublicKey(mintAddress),
					accountAuthority,
					true,
					TOKEN_PROGRAM_LOOKUP[tokenProgramLabel]
				);

				return (
					expectedTokenAccount.toString() === accountInfo.pubkey.toString()
				);
			}
		},
		[]
	);

	const processTokenAccount = useCallback(
		(
			accountInfo: TokenAccountResponse
		): { mintAddress: string; account: TokenAccountInfo } | null => {
			if (!authority) return null;

			const parsedInfo = accountInfo.account.data.parsed
				.info as ParsedTokenInfo;
			const mintAddress = parsedInfo.mint;

			const mintIsForVelocitySpotMarket =
				velocitySpotMarketMints.has(mintAddress);

			if (mintIsForVelocitySpotMarket) {
				addTokenAccountToBadStateTracking(mintAddress, accountInfo);
			}

			if (shouldSyncTokenAccount(mintAddress, accountInfo as any, authority)) {
				const account: TokenAccountInfo = {
					pubkey: accountInfo.pubkey,
					mint: new PublicKey(mintAddress),
					programId: TOKEN_PROGRAM_ID,
					parsedBalance: parsedInfo.tokenAmount.uiAmount,
				};

				handledMintsToTokenAccounts.current.set(
					mintAddress,
					accountInfo.pubkey.toBase58()
				);

				dlog(
					`spl_subscription`,
					`syncing_token_account :: ${mintAddress}:${accountInfo.pubkey.toBase58()} => ${
						account.parsedBalance
					}`
				);

				return { mintAddress, account };
			}

			return null;
		},
		[authority, shouldSyncTokenAccount]
	);

	const fetchAndProcessTokenAccountsForProgram = useCallback(
		async (
			programConnection: Connection,
			programAuthority: PublicKey,
			programId: PublicKey
		): Promise<{ mintAddress: string; account: TokenAccountInfo }[]> => {
			try {
				const response = await programConnection.getParsedTokenAccountsByOwner(
					programAuthority,
					{ programId }
				);
				const processedAccounts = response.value.map(processTokenAccount);
				const filteredProcessedAccounts = processedAccounts.filter(
					(account) => account !== null
				);

				return filteredProcessedAccounts;
			} catch (error) {
				console.warn(
					'Error parsing and fetching token accounts:',
					error,
					'\n falling back to manual parsing'
				);
				// Back off briefly before the fallback — the usual failure here is a
				// rate-limited page-load burst, and retrying into the same burst
				// just fails again.
				await sleep(TOKEN_ACCOUNT_FALLBACK_DELAY_MS);
				// fallback to parsing the token accounts manually
				const raw = await programConnection.getTokenAccountsByOwner(
					programAuthority,
					{
						programId,
					}
				);
				return raw.value.map<{
					mintAddress: string;
					account: TokenAccountInfo;
				}>(({ pubkey, account }) => {
					const unpacked = unpackAccount(pubkey, account, programId);
					const accountInfoParsed: TokenAccountInfo = {
						pubkey,
						mint: unpacked.mint,
						programId,
						parsedBalance: Number(unpacked.amount.toString()),
					};
					return {
						mintAddress: unpacked.mint.toBase58(),
						account: accountInfoParsed,
					};
				});
			}
		},
		[processTokenAccount]
	);

	const fetchAndProcessAllTokenAccounts = useCallback(async () => {
		if (!authority || !connection) return;

		const processedAccounts = (
			await Promise.all(
				TOKEN_PROGRAMS_TO_FETCH.map((programId) =>
					fetchAndProcessTokenAccountsForProgram(
						connection,
						authority,
						programId
					)
				)
			)
		)
			.flat()
			.filter(Boolean);

		setTokenAccountsStore((s) => {
			const newTokenAccountsMap: TokenAccountMap = {};

			processedAccounts.forEach(({ mintAddress, account }) => {
				newTokenAccountsMap[mintAddress] = account;
			});

			s.tokenAccounts = newTokenAccountsMap;
		});

		setLastSyncedWallet(authority);
	}, [
		authority,
		connection,
		fetchAndProcessTokenAccountsForProgram,
		setLastSyncedWallet,
	]);

	// Sync when wallet changes
	useEffect(() => {
		fetchAndProcessAllTokenAccounts();
	}, [authority, fetchAndProcessAllTokenAccounts]);

	// Listen for refresh events
	useEffect(() => {
		if (!eventEmitter) return;

		eventEmitter.on('refreshTokenAccounts', fetchAndProcessAllTokenAccounts);
		return () => {
			eventEmitter.off('refreshTokenAccounts', fetchAndProcessAllTokenAccounts);
		};
	}, [eventEmitter, fetchAndProcessAllTokenAccounts]);
};
